import React, { useState, useEffect } from "react";
import {
  Tree,
  TreeItem,
  TreeItemLayout,
  Link,
  makeStyles,
  Button as FluentButton,
  Spinner,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
} from "@fluentui/react-components";
import ConfirmationForm from "./ConfirmationForm";
import { Delete20Regular } from "@fluentui/react-icons";
import Button from "./Button";
import { fileName } from "../utils";
import api from "../../api/api";

type ReportsTreeProps = {
  authToken: string;
  identityProvider: string;
  shouldRefresh?: Date;
};

type ReportInstance = {
  id: string;
  name: string;
  url: string;
  token: string;
};

type ReportTemplate = {
  id: string;
  name: string;
  url: string;
  token: string;
  instances: ReportInstance[];
};

const useStyles = makeStyles({
  spinnerContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  reportTemplate: {
    color: "#001C71",
  },
  reportInstance: {
    color: "#6D6E71",
  },
  searchInput: {
    width: "100%",
    fontSize: "inherit",
    border: "0.25pt solid #C4E1E0",
    borderRadius: "6px",
    "&:hover": {
      border: "0.25pt solid #7FBDBA",
    },
  },
});

const openExcelFromBase64 = async (base64String: string) => {
  await Excel.run(async (context) => {
    Excel.createWorkbook(base64String);
    await context.sync();
  });
};

const fetchAndOpenReportTemplate = async (
  url: string,
  token: string,
  authToken: string,
  identityProvider: string,
  setPercent: (number) => void
) => {
  setPercent(0);
  try {
    await downloadFileWithProgress(
      `report/file/stream?CompleteUrl=${encodeURIComponent(url)}&SasToken=${encodeURIComponent(token)}`,
       authToken, identityProvider, setPercent)
      .then(async (fileContent: Uint8Array) => {
        const excelContentBase64 = btoa(
          Array.from(fileContent).map(code => String.fromCharCode(code)).join(''));
        await openExcelFromBase64(excelContentBase64);
        console.log("Excel report file contents loaded successful.");
      });
  } catch (error) {
    throw new Error("Error fetching report file contents: " + error);
  } finally {
    setPercent(0);
  }
};

const fetchAndOpenReportInstance = async (
  name: string,
  url: string,
  token: string,
  authToken: string,
  identityProvider: string,
  setPercent: (number) => void
) => {
  setPercent(0);
  try {
    await downloadFileWithProgress(
      `report/file/stream?FileName=${encodeURIComponent(name)}&ContainerUrl=${encodeURIComponent(url)}&CompleteUrl=${encodeURIComponent(url)}&SasToken=${encodeURIComponent(token)}`,
       authToken, identityProvider, setPercent)
      .then(async (fileContent: Uint8Array) => {
        const excelContentBase64 = btoa(
          Array.from(fileContent).map(code => String.fromCharCode(code)).join(''));
        await openExcelFromBase64(excelContentBase64);
        console.log("Excel report instance file contents loaded successful.");
      });
  } catch (error) {
    throw new Error("Error fetching report instance file contents: " + error);
  } finally {
    setPercent(0);
  }
};

const downloadFileWithProgress = async function (
  url: string,
  authToken: string,
  identityProvider: string,
  setPercent: (number) => void
): Promise<Uint8Array | null> {
  const response = await api.fetch(url, authToken, identityProvider);

  if (!response.ok) throw new Error('Download failed');

  const contentLength = response.headers.get('Content-Length');
  if (!contentLength) {
    console.warn('Server did not provide Content-Length. No progress possible.');
    return null;
  }

  const total = parseInt(contentLength, 10);
  let loaded = 0;

  const reader = response.body.getReader();
  const chunks = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    loaded += value.length;

    const percent = ((loaded / total) * 100).toFixed(2);
    console.log(`Downloaded ${percent}%`);
    setPercent(parseFloat(percent));
  }

  // combine chunks into a single Uint8Array
  const allChunks = new Uint8Array(loaded);
  let position = 0;
  for (const chunk of chunks) {
    allChunks.set(chunk, position);
    position += chunk.length;
  }
  return allChunks;
}

const ReportsTree: React.FC<ReportsTreeProps> = ({ authToken, identityProvider, shouldRefresh }) => {
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [areReportsLoading, setAreReportsLoading] = useState(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = useState(false);
  const [currentReportInstance, setCurrentReportInstance] = useState<ReportInstance>();
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNodeId, setExpandedNodeId] = useState(null);
  const [error, setError] = useState<string>("");
  const [percent, setPercent] = useState<number>(0);

  const styles = useStyles();
  useEffect(() => {
    const fetchReportTemplates = async () => {
      try {
        setAreReportsLoading(true);
        const response = await api.fetch("reports", authToken, identityProvider);
        if (!response.ok) throw new Error("Response not OK");
        const data: any = await response.json();
        const reportTemplates = data.map((row) => ({
          id: row.id,
          name: row.name,
          url: row.url,
          token: row.sasToken,
          instances:
            row.excelFiles?.map((row) => ({
              id: row.id,
              name: row.name,
              url: row.url,
              token: row.sasToken,
            })) || [],
        }));
        setReportTemplates(reportTemplates);
      } catch (error) {
        setError("Error fetching report templates: " + error);
      } finally {
        setAreReportsLoading(false);
      }
    };
    fetchReportTemplates();
  }, [shouldRefresh]);

  const handleToggle = (nodeId: string) => {
    setSearchQuery("");
    setExpandedNodeId((prevExpandedNodeId) => (prevExpandedNodeId === nodeId ? null : nodeId));
  };

  const handleReportTemplateClick = async (e: Event, reportTemplate: ReportTemplate) => {
    e.preventDefault();
    setIsReportDownloading(true);
    console.log("Opening report: " + reportTemplate.id);
    try {
      await fetchAndOpenReportTemplate(reportTemplate.url, reportTemplate.token, authToken, identityProvider, setPercent);
    } catch (error) {
      setError(error);
    }
    setTimeout(() => {
      setIsReportDownloading(false);
    }, 1000);
  };
  const handleReportClick = async (e: Event, reportInstance: ReportInstance) => {
    e.preventDefault();
    setIsReportDownloading(true);
    console.log("Opening report instance: " + reportInstance.id);
    try {
      await fetchAndOpenReportInstance(
        reportInstance.name,
        reportInstance.url,
        reportInstance.token,
        authToken,
        identityProvider,
        setPercent
      );
    } catch (error) {
      setError(error);
    }
    setTimeout(() => {
      setIsReportDownloading(false);
    }, 1000);
  };
  const handleRemoveClick = (reportInstance: ReportInstance) => {
    setCurrentReportInstance(reportInstance);
    setIsRemoveConfirmationOpen(true);
  };
  const handleRemoveConfirmationClick = async (reportInstance: ReportInstance) => {
    try {
      await api.delete(
        `report/file?ReportFileId=${encodeURIComponent(reportInstance.id)}`,
        authToken,
        identityProvider
      );
    } catch (error) {
      setError("Error removing report instance: " + error);
    }

    setIsRemoveConfirmationOpen(false);
    console.log("Removed report instance: " + reportInstance.id);

    for (let reportTemplate of reportTemplates) {
      reportTemplate.instances = reportTemplate.instances.filter((t) => t != reportInstance);
    }
    setReportTemplates(reportTemplates);
  };
  const handleRemoveCancelClick = () => {
    setIsRemoveConfirmationOpen(false);
  };

  return (
    <div>
      {areReportsLoading && <div style={{ marginBottom: "8px" }}>Loading...</div>}
      <Tree aria-label="Reports tree">
        {reportTemplates
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((reportTemplate) => (
          <TreeItem
            itemType={reportTemplate.instances.length > 0 ? "branch" : "leaf"}
            key={reportTemplate.id}
            onOpenChange={() => handleToggle(reportTemplate.id)}
            open={expandedNodeId === reportTemplate.id}
          >
            <TreeItemLayout>
              <Link className={styles.reportTemplate} onClick={(e) => handleReportTemplateClick(e, reportTemplate)}>
                {fileName(reportTemplate.name)}
              </Link>
            </TreeItemLayout>
            <Tree>
              <div style={{ paddingLeft: "24px" }}>
                <Input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search"
                  value={searchQuery}
                  size="small"
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div style={{ maxHeight: "100px", overflow: "auto" }}>
                  {reportTemplate.instances
                    .filter(
                      (
                        reportInstance //reportInstance?.name?.toLowerCase().includes(searchQuery.toLowerCase())
                      ) => fileName(reportInstance.name).toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((reportInstance) => (
                      <TreeItem itemType="leaf" key={reportInstance.id}>
                        <TreeItemLayout
                          style={{ paddingLeft: "4px" }}
                          actions={
                            <FluentButton
                              appearance="subtle"
                              icon={<Delete20Regular />}
                              onClick={() => handleRemoveClick(reportInstance)}
                            />
                          }
                        >
                          <Link className={styles.reportInstance} onClick={(e) => handleReportClick(e, reportInstance)}>
                            {fileName(reportInstance.name)}
                          </Link>
                        </TreeItemLayout>
                      </TreeItem>
                    ))}
                </div>
              </div>
            </Tree>
          </TreeItem>
        ))}
      </Tree>
      {reportTemplates.length == 0 && !areReportsLoading && <div>No analyzers available.</div>}
      <ConfirmationForm
        isOpen={isRemoveConfirmationOpen}
        onConfirm={() => handleRemoveConfirmationClick(currentReportInstance)}
        onCancel={handleRemoveCancelClick}
        dialogTitle="Remove Report"
        dialogContent="Are you sure you want to remove the report?"
        confirmButtonLabel="Remove"
        cancelButtonLabel="Cancel"
      />
      {isReportDownloading && (
        <div className={styles.spinnerContainer}>
          <Spinner size="large" />
          {percent > 0 && (
            <p style={{ color: "white", marginLeft: "20px" }}>
              Downloading... {`${percent.toFixed(0)}%`}
            </p>
          )}
        </div>
      )}
      <div style={{ marginTop: "20px", borderTop: "1px solid #f0f0f0", paddingTop: "10px" }}>
        <p>Click on a BuilderMetrix® Analyzer to open it in a new window and use the BuilderMetrix® button (Show Analyzer Pane) to set parameters and get the latest data.</p>
      </div>
      <Dialog open={(error?.length ?? 0) > 0}>
        <DialogSurface style={{ margin: "16px" }}>
          <DialogBody>
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <p style={{ color: "red" }}>
                An error has occurred:
                <br />
                {error}
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button onClick={() => setError("")} label="Close" />
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ReportsTree;
